import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Building2, Mail, Lock, Phone, MapPin, FileText, ArrowRight, CheckCircle } from 'lucide-react';
import { formatError } from '../utils/errorUtils';

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    company_name: '',
    rera_number: '',
    phone: '',
    address: '',
    about: ''
  });
  const { registerAgency } = useAuth();
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStep1 = (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { confirmPassword, ...data } = formData;
      await registerAgency(data);
      toast.success('Welcome to EstateCloud!');
      navigate('/dashboard');
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-50 flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1760067537255-64d36262cb0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjB2aWxsYSUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MDQwNzA2M3ww&ixlib=rb-4.1.0&q=85"
          alt="Modern villa"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/80 to-navy-900/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="space-y-6">
            <h2 className="text-3xl font-heading font-bold text-white">
              Join EstateCloud Today
            </h2>
            <ul className="space-y-4">
              {[
                'Create your professional agency website',
                'Showcase unlimited properties',
                'Receive direct client enquiries',
                'Track performance with analytics'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3 text-white/90">
                  <CheckCircle className="w-5 h-5 text-sky-400 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="logo-link">
            <Building2 className="w-8 h-8 text-sky-500" />
            <span className="text-xl font-heading font-bold text-navy-900">EstateCloud</span>
          </Link>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-sky-500' : 'text-navy-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-sky-500 text-white' : 'bg-navy-200'}`}>
                1
              </div>
              <span className="text-sm font-medium">Account</span>
            </div>
            <div className="flex-1 h-px bg-navy-200" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-sky-500' : 'text-navy-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-sky-500 text-white' : 'bg-navy-200'}`}>
                2
              </div>
              <span className="text-sm font-medium">Company</span>
            </div>
          </div>

          {step === 1 && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-heading font-bold text-navy-900 mb-2">Create Your Account</h1>
                <p className="text-navy-600 text-sm">Enter your email and create a password</p>
              </div>

              <form onSubmit={handleStep1} className="space-y-5" data-testid="register-step1-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-navy-700">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-navy-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => updateField('password', e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-navy-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => updateField('confirmPassword', e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full h-11 bg-navy-900 hover:bg-navy-800" data-testid="continue-btn">
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-heading font-bold text-navy-900 mb-2">Company Details</h1>
                <p className="text-navy-600 text-sm">Tell us about your real estate agency</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-step2-form">
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-navy-700">Company Name</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="ABC Real Estate"
                      value={formData.company_name}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rera_number" className="text-navy-700">RERA Number <span className="text-navy-400 text-xs">(Optional)</span></Label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="rera_number"
                      type="text"
                      placeholder="RERA/MH/123456"
                      value={formData.rera_number}
                      onChange={(e) => updateField('rera_number', e.target.value)}
                      className="pl-10 h-11"
                      data-testid="rera-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-navy-700">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="9876543210"
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        updateField('phone', val);
                      }}
                      className="pl-10 h-11"
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                      title="Please enter a valid 10-digit phone number"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-navy-700">Office Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-5 h-5 text-navy-400" />
                    <Input
                      id="address"
                      type="text"
                      placeholder="123 Business Park, Mumbai"
                      value={formData.address}
                      onChange={(e) => updateField('address', e.target.value)}
                      className="pl-10 h-11"
                      required
                      data-testid="address-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="about" className="text-navy-700">About Company (Optional)</Label>
                  <Textarea
                    id="about"
                    placeholder="Tell us about your company..."
                    value={formData.about}
                    onChange={(e) => updateField('about', e.target.value)}
                    className="min-h-[80px]"
                    data-testid="about-input"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11"
                    onClick={() => setStep(1)}
                    data-testid="back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-11 bg-navy-900 hover:bg-navy-800"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Creating...' : 'Create Account'}
                  </Button>
                </div>
              </form>
            </>
          )}

          <p className="mt-6 text-center text-navy-600 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-sky-500 font-medium hover:text-sky-600" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
